import { useState } from 'react';
import { MessageSquare, Send, User, Search } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const Messages = () => {
  const { user } = useAuth();
  const [selectedChat, setSelectedChat] = useState(null);
  const [message, setMessage] = useState('');

  // Mock conversations - in production this would come from API
  const conversations = [];

  const handleSendMessage = () => {
    if (!message.trim()) return;
    toast.info('Messaging feature coming soon!');
    setMessage('');
  };

  return (
    <DashboardLayout currentPage="/dashboard/messages">
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">Messages</h1>
        <p className="text-white/50">Communicate with our team about your projects</p>
      </div>

      <div className="bg-[#12121A] border border-white/10 rounded-2xl overflow-hidden" style={{ height: 'calc(100vh - 280px)', minHeight: '500px' }}>
        <div className="grid md:grid-cols-3 h-full">
          {/* Conversations List */}
          <div className="border-r border-white/10">
            {/* Search */}
            <div className="p-4 border-b border-white/10">
              <div className="relative">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <input
                  type="text"
                  placeholder="Search messages..."
                  className="w-full bg-[#0A0A0F] border border-white/10 rounded-xl pl-10 pr-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#3B82F6] transition-all"
                />
              </div>
            </div>

            {/* Conversation List */}
            <div className="overflow-y-auto" style={{ height: 'calc(100% - 73px)' }}>
              {conversations.length === 0 ? (
                <div className="p-6 text-center">
                  <div className="w-16 h-16 bg-white/5 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <MessageSquare size={28} className="text-white/20" />
                  </div>
                  <p className="text-white/50 text-sm">No conversations yet</p>
                  <p className="text-white/30 text-xs mt-1">Start a conversation by creating a project</p>
                </div>
              ) : (
                conversations.map((conv) => (
                  <div
                    key={conv.id}
                    onClick={() => setSelectedChat(conv)}
                    className={`p-4 border-b border-white/5 cursor-pointer transition-all ${
                      selectedChat?.id === conv.id ? 'bg-[#3B82F6]/10' : 'hover:bg-white/5'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] rounded-full flex items-center justify-center">
                        <User size={18} className="text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <h4 className="text-white font-medium text-sm truncate">{conv.name}</h4>
                          <span className="text-white/40 text-xs">{conv.time}</span>
                        </div>
                        <p className="text-white/50 text-xs truncate">{conv.lastMessage}</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Chat Area */}
          <div className="md:col-span-2 flex flex-col">
            {selectedChat ? (
              <>
                {/* Chat Header */}
                <div className="p-4 border-b border-white/10 flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] rounded-full flex items-center justify-center">
                    <User size={18} className="text-white" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">{selectedChat.name}</h4>
                    <p className="text-white/50 text-xs">Online</p>
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 p-4 overflow-y-auto">
                  {/* Messages would render here */}
                </div>

                {/* Input */}
                <div className="p-4 border-t border-white/10">
                  <div className="flex gap-3">
                    <input
                      type="text"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Type a message..."
                      className="flex-1 bg-[#0A0A0F] border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#3B82F6] transition-all"
                      onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                    />
                    <button
                      onClick={handleSendMessage}
                      className="px-4 rounded-xl transition-all hover:scale-105"
                      style={{ background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)' }}
                    >
                      <Send size={20} className="text-white" />
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-20 h-20 bg-white/5 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <MessageSquare size={36} className="text-white/20" />
                  </div>
                  <h3 className="text-white font-semibold mb-2">Select a conversation</h3>
                  <p className="text-white/50 text-sm">Choose a conversation to start messaging</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Messages;
